import { splitCopyTargetsByLocationType } from '@/allocation/allocation';
import { UpdateAllocationReturn } from '@/allocation/composables/useUpdateAllocations';
import auth from '@/auth/auth';
import { ACTIVE_STATES } from '@/components/AllocationVariantSelectionPanel/types';
import {
    AllocationPhaseDeleteInput,
    AllocationPhaseInput,
    AllocationPhaseUpdateInput,
    AllocationPhaseUpdateInputFlat,
    AllocationUsage,
    CopyTargetLocation,
    TimelineVariant,
} from '@/components/Timeline/allocation/types';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import { validateAllocationUpdateResponse } from '@/components/Timeline/timeline';
import { useTimelineStore } from '@/components/Timeline/Timeline.store';
import {
    AllocationPhaseUpdate,
    LocationTypeCategory,
    PhaseTypeDatabase,
    TIMELINE_AREA_TYPES,
    TIMELINE_ROOM_TYPES,
} from '@/components/Timeline/Timeline.types';
import {
    AllocationAreaDuplicateDocument,
    AllocationAreaUpdateAndDuplicateDocument,
    AllocationRoomDuplicateDocument,
    AllocationRoomUpdateAndDuplicateDocument,
    CreateEventAllocationAreaDocument,
    CreateEventAllocationRoomDocument,
    EventAllocationAreaUpdateDocument,
    EventAllocationRoomUpdateDocument,
    ExternalEventAllocationUsageType,
    GetEventAllocationsForEventDocument,
    GetEventDataForTimelineDocument,
    TimelineAreaFragment,
    TimelineEventFragment,
    TimelineFullSiteFragment,
    TimelineRoomFragment,
    UpdateEventVariantReferenceAllocationIdDocument,
} from '@/generated/graphql';
import { routeForLocation } from '@/location/location';
import { insideRangeCheck } from '@/utils/date';
import { useApolloClient, useMutation, useQuery } from '@vue/apollo-composable';
import { DateTime } from 'luxon';
import { computed, Ref, ref, toValue, watch } from 'vue';
import { mapEventAllocationForTimeline, mapEventVariantForTimeline } from '../event';

const EMPTY_SITE = {
    id: '',
    nameShort: '',
    buildings: {
        nodes: [],
    },
    zones: {
        nodes: [],
    },
};

export function useEventTimelineData(
    eventId: Ref<string>,
    dateTimeFromRoute: Ref<DateTime | undefined>,
    timelineStart: Ref<DateTime | undefined>,
    timelineEnd: Ref<DateTime | undefined>,
    setSelectedStartDateTime: (value: DateTime) => { newTimelineStart: DateTime; newTimelineEnd: DateTime },
) {
    const variantsRef: Ref<TimelineVariant[]> = ref([]);
    let variantsManuallyUpdated = false;
    const event: Ref<TimelineEventFragment | undefined> = ref();
    const rooms: Ref<TimelineRoomFragment[]> = ref([]);
    const areas: Ref<TimelineAreaFragment[]> = ref([]);
    const site: Ref<TimelineFullSiteFragment> = ref(EMPTY_SITE);
    const defaultPhaseTypeId = ref('');
    const executionPhasePosition = ref(4);
    const usages: Ref<AllocationUsage[]> = ref([]);

    const client = useApolloClient().client;
    const store = useTimelineStore();
    const timelineDataProcessing = ref(false);

    const { loading, error, onResult } = useQuery(
        GetEventDataForTimelineDocument,
        () => ({
            eventId: toValue(eventId),
            activeStates: ACTIVE_STATES,
            roomTypes: TIMELINE_ROOM_TYPES,
            areaTypes: TIMELINE_AREA_TYPES,
        }),
        () => ({
            enabled: !!toValue(eventId),
            fetchPolicy: 'network-only',
        }),
    );

    const areAllocationsEditable = auth.isAdmin();

    onResult((result) => {
        if (result.error) {
            throw new Error('Error on receiving event timeline data');
        }
        const data = result.data;
        if (data) {
            timelineDataProcessing.value = true;
            try {
                let start = timelineStart.value;
                let end = timelineEnd.value;

                event.value = data.event ?? undefined;
                rooms.value = data.rooms?.nodes ?? [];
                areas.value = data.areas?.nodes ?? [];
                const sites = data.sites?.nodes ?? [];
                if (!sites) throw new Error(`Missing site for event: ${toValue(eventId)}`);
                site.value = sites[0];

                usages.value =
                    data.usages?.nodes.map((u) => ({
                        ...u,
                        type: u.type as PhaseTypeDatabase,
                        color: mapToPhaseColor(u.type as Exclude<PhaseTypeDatabase, ExternalEventAllocationUsageType>),
                    })) ?? [];
                const executionPhase = data.usages?.nodes.find((u) => u.isExecution);
                if (!executionPhase) {
                    throw Error('No execution phase exists. Wrong data');
                }
                defaultPhaseTypeId.value = executionPhase.id;
                executionPhasePosition.value = executionPhase.position;

                if (!data.event) {
                    throw new Error(`Event not found: ${toValue(eventId)}`);
                }
                const variants = data.event.variants.nodes;
                if (variants.length === 0) {
                    throw new Error(`Event has no variants: ${toValue(eventId)}`);
                }

                // set timelinestore total count for locations map
                const allAllocationRooms = data.allocationRooms?.nodes ?? [];
                const allAllocationAreas = data.allocationAreas?.nodes ?? [];

                const countsMap = [...allAllocationRooms, ...allAllocationAreas].reduce((map, { locationId }) => {
                    return map.set(locationId, (map.get(locationId) ?? 0) + 1);
                }, new Map<string, number>());

                const referenceAllocationIds = variants.flatMap(
                    (v) => v.referenceAllocationRoomId ?? v.referenceAllocationAreaId ?? [],
                );
                countsMap.set('reference-allocation-row', referenceAllocationIds.length);
                store.setLocationAllocationTotalCounts(countsMap);

                // only set start/end DateTime if there is none initially set
                if (start === undefined && end === undefined) {
                    // start default now only if nothing else is available for calculation
                    let initialStart: DateTime = DateTime.now();
                    if (dateTimeFromRoute.value) {
                        initialStart = dateTimeFromRoute.value;
                    } else {
                        const roomAllocationsStart = result.data.firstAllocationRoom?.nodes.map((n) =>
                            DateTime.fromISO(n.start),
                        )[0];
                        const areaAllocationsStart = result.data.firstAllocationArea?.nodes.map((n) =>
                            DateTime.fromISO(n.start),
                        )[0];
                        if (roomAllocationsStart && !areaAllocationsStart && roomAllocationsStart.isValid) {
                            initialStart = roomAllocationsStart;
                        } else if (!roomAllocationsStart && areaAllocationsStart && areaAllocationsStart.isValid) {
                            initialStart = areaAllocationsStart;
                        } else if (
                            roomAllocationsStart &&
                            areaAllocationsStart &&
                            roomAllocationsStart.isValid &&
                            areaAllocationsStart.isValid
                        ) {
                            initialStart = DateTime.min(roomAllocationsStart, areaAllocationsStart);
                        } else if (!roomAllocationsStart && !areaAllocationsStart) {
                            if (result.data.event?.executionYear) {
                                const yearStart = DateTime.fromObject({ year: result.data.event.executionYear });
                                if (yearStart.isValid) {
                                    initialStart = yearStart;
                                }
                            }
                        }
                    }

                    const { newTimelineStart, newTimelineEnd } = setSelectedStartDateTime(initialStart);
                    start = newTimelineStart;
                    end = newTimelineEnd;
                }

                // map variants
                if (!variantsManuallyUpdated) {
                    if (variants) {
                        variantsRef.value = variants.map((variant) =>
                            mapEventVariantForTimeline(
                                variant,
                                start,
                                end,
                                executionPhasePosition.value,
                                areAllocationsEditable,
                            ),
                        );
                    }
                }
            } finally {
                timelineDataProcessing.value = false;
            }
        }
    });

    watch([timelineStart, timelineEnd], async ([newStart, newEnd]) => {
        const start = timelineStart.value;
        const end = timelineEnd.value;

        if (start && end && newStart && newEnd) {
            timelineDataProcessing.value = true;
            try {
                if (!event.value) {
                    throw new Error('Event not loaded');
                }
                const currentEvent = event.value;

                const result = await client.query({
                    query: GetEventAllocationsForEventDocument,
                    variables: { id: eventId.value },
                });

                const allocationRooms = result.data.allocationRooms?.nodes ?? [];
                const allocationAreas = result.data.allocationAreas?.nodes ?? [];

                if (allocationRooms || allocationAreas) {
                    // update timeline range filtering
                    variantsRef.value = variantsRef.value.map((variant) => ({
                        ...variant,
                        allocations: {
                            nodes: [
                                ...allocationRooms
                                    .filter(
                                        (n) =>
                                            n.variant.id === variant.id &&
                                            insideRangeCheck(
                                                DateTime.fromISO(n.start),
                                                DateTime.fromISO(n.end),
                                                newStart,
                                                newEnd,
                                            ),
                                    )
                                    .map((n) =>
                                        mapEventAllocationForTimeline(
                                            {
                                                ...variant,
                                                referenceAllocationRoomId: n.variant.referenceAllocationRoomId ?? null,
                                                referenceAllocationAreaId: n.variant.referenceAllocationAreaId ?? null,
                                                event: {
                                                    id: currentEvent.id,
                                                    nameShort: currentEvent.nameShort,
                                                },
                                            },
                                            n,
                                            'Room',
                                            executionPhasePosition.value,
                                            areAllocationsEditable,
                                        ),
                                    ),
                                ...allocationAreas
                                    .filter(
                                        (n) =>
                                            n.variant.id === variant.id &&
                                            insideRangeCheck(
                                                DateTime.fromISO(n.start),
                                                DateTime.fromISO(n.end),
                                                newStart,
                                                newEnd,
                                            ),
                                    )
                                    .map((n) =>
                                        mapEventAllocationForTimeline(
                                            {
                                                ...variant,
                                                referenceAllocationRoomId: null,
                                                referenceAllocationAreaId: variant.referenceAllocationId,
                                                event: {
                                                    id: currentEvent.id,
                                                    nameShort: currentEvent.nameShort,
                                                },
                                            },
                                            n,
                                            'Area',
                                            executionPhasePosition.value,
                                            areAllocationsEditable,
                                        ),
                                    ),
                            ],
                        },
                    }));
                }
            } finally {
                timelineDataProcessing.value = false;
            }
        }
    });

    function setVariantsRef(value: TimelineVariant[]) {
        variantsManuallyUpdated = true;
        variantsRef.value = value;
    }

    const timelineHeaderName = computed(() => site.value.nameShort ?? '');
    const timelineHeaderLinkTarget = computed(() => routeForLocation('Site', site.value.id));

    const createRoomMutation = useMutation(CreateEventAllocationRoomDocument, { fetchPolicy: 'no-cache' });
    const createAreaMutation = useMutation(CreateEventAllocationAreaDocument, { fetchPolicy: 'no-cache' });

    async function createAllocation(
        variantId: string,
        phases: AllocationPhaseInput[],
        locationId: string,
        startDateTime: DateTime,
        endDateTime: DateTime,
        locationTypeCategory: LocationTypeCategory,
    ) {
        let mutation = undefined;
        switch (locationTypeCategory) {
            case 'Room':
                mutation = createRoomMutation;
                break;
            case 'Area':
                mutation = createAreaMutation;
                break;
        }

        const start = startDateTime.toISO();
        if (!start) {
            throw Error('Conversion toISO() failed for startDateTime');
        }

        const end = endDateTime.toISO();
        if (!end) {
            throw Error('Conversion toISO() failed for endDateTime');
        }

        const result = await mutation?.mutate({
            variantId,
            phases,
            locationId,
            start,
            end,
        });

        if (result?.errors) {
            throw new Error('Could not create allocation');
        }

        if (result?.data) {
            const allocation = result.data.create?.allocation;
            if (allocation) {
                store.incrementLocationAllocationTotalCount(allocation.locationId);
                const updatedVariants = variantsRef.value.map((v) => {
                    if (v.id === variantId) {
                        return {
                            ...v,
                            allocationsTotalCount: v.allocationsTotalCount + 1,
                            allocations: {
                                nodes: [
                                    ...v.allocations.nodes,
                                    mapEventAllocationForTimeline(
                                        {
                                            ...v,
                                            referenceAllocationRoomId:
                                                allocation.variant.referenceAllocationRoomId ?? null,
                                            referenceAllocationAreaId:
                                                allocation.variant.referenceAllocationAreaId ?? null,
                                            event: {
                                                id: event.value?.id ?? '',
                                                nameShort: event.value?.nameShort ?? '',
                                            },
                                        },
                                        allocation,
                                        locationTypeCategory,
                                        executionPhasePosition.value,
                                        areAllocationsEditable,
                                    ),
                                ],
                            },
                        };
                    }
                    return v;
                });
                setVariantsRef(updatedVariants);
                return { createdAllocationId: allocation.id, updatedVariantsRef: updatedVariants };
            }
        }
    }

    const updateRoomMutation = useMutation(EventAllocationRoomUpdateDocument, { fetchPolicy: 'no-cache' });
    const updateAreaMutation = useMutation(EventAllocationAreaUpdateDocument, { fetchPolicy: 'no-cache' });
    const updateReferenceAlloctionMutation = useMutation(UpdateEventVariantReferenceAllocationIdDocument, {
        fetchPolicy: 'no-cache',
    });

    async function updateAllocationData(
        variantId: string,
        allocationId: string,
        locationTypeCategory: LocationTypeCategory,
        start: DateTime,
        end: DateTime,
        isOptional: boolean,
        createPhases: AllocationPhaseInput[],
        updatePhases: AllocationPhaseUpdateInput[],
        deletePhases: AllocationPhaseDeleteInput[],
        remainingPhases: (AllocationPhaseUpdate & { dayOffsetCount: number })[],
        referenceAllocationId?: string | null,
    ): Promise<UpdateAllocationReturn> {
        if (referenceAllocationId !== undefined) {
            const currentVariants = variantsRef.value;
            const variantToUpdate = currentVariants.find((variant) => variant.id === variantId);
            if (variantToUpdate) {
                let referenceAllocationAreaId = null;
                let referenceAllocationRoomId = null;
                if (referenceAllocationId !== null) {
                    const refAllocation = variantToUpdate.allocations.nodes.find(
                        (allocation) => allocation.id === referenceAllocationId,
                    );
                    if (refAllocation) {
                        referenceAllocationAreaId =
                            refAllocation.locationTypeCategory === 'Room' ? null : referenceAllocationId;
                        referenceAllocationRoomId =
                            refAllocation.locationTypeCategory === 'Room' ? referenceAllocationId : null;
                    }
                }
                const response = await updateReferenceAlloctionMutation.mutate({
                    variantId,
                    referenceAllocationAreaId,
                    referenceAllocationRoomId,
                });
                if (response?.errors) {
                    throw Error(`Could not update variant reference allocatio id ${response.errors}`);
                }
                if (response?.data) {
                    // Refresh every copy of the id, not just the variant's own field. Each allocation
                    // carries one on `allocationVariant`, baked in when the variant was mapped, and the
                    // Timeline store is seeded from it — so leaving it stale made the reference revert
                    // whenever a variant's allocations all unmounted and remounted.
                    const updatedVariants = currentVariants.map((v) =>
                        v.id === variantId
                            ? {
                                  ...v,
                                  referenceAllocationId,
                                  allocations: {
                                      ...v.allocations,
                                      nodes: v.allocations.nodes.map((allocation) => ({
                                          ...allocation,
                                          allocationVariant: {
                                              ...allocation.allocationVariant,
                                              referenceAllocationId,
                                          },
                                      })),
                                  },
                              }
                            : v,
                    );
                    setVariantsRef(updatedVariants);
                }
            }
        }

        if (locationTypeCategory === 'Room') {
            const currentVariants = variantsRef.value;
            const response = await updateRoomMutation.mutate({
                allocationId,
                start: start.toISO(),
                end: end.toISO(),
                isOptional,
                createPhases,
                updatePhases,
                deletePhases,
            });

            if (response && response.data) {
                const updatedAllocation = response.data.update?.allocation;

                if (updatedAllocation) {
                    //validate result
                    const confirmedPhases = validateAllocationUpdateResponse(updatedAllocation, remainingPhases);
                    if (isOptional !== updatedAllocation.isOptional) {
                        throw Error(`isOptional of allocation ${updatedAllocation.id} not updated correctly`);
                    }

                    const updatedVariants = currentVariants.map((v) => {
                        if (v.id === variantId) {
                            return {
                                ...v,
                                allocations: {
                                    nodes: v.allocations.nodes.map((n) => {
                                        if (n.id === updatedAllocation.id) {
                                            return mapEventAllocationForTimeline(
                                                {
                                                    ...v,
                                                    referenceAllocationRoomId:
                                                        updatedAllocation.variant.referenceAllocationRoomId ?? null,
                                                    referenceAllocationAreaId:
                                                        updatedAllocation.variant.referenceAllocationAreaId ?? null,
                                                    event: {
                                                        id: event.value?.id ?? '',
                                                        nameShort: event.value?.nameShort ?? '',
                                                    },
                                                },
                                                updatedAllocation,
                                                locationTypeCategory,
                                                executionPhasePosition.value,
                                                areAllocationsEditable,
                                            );
                                        }
                                        return n;
                                    }),
                                },
                            };
                        } else {
                            return v;
                        }
                    });
                    setVariantsRef(updatedVariants);

                    const result = {
                        phaseUpdates: confirmedPhases,
                        isOptional,
                        referenceAllocationId: referenceAllocationId ?? null,
                    };
                    return result;
                }
            }
            throw Error('Error on allocationRoom update without duplicate');
        }

        if (locationTypeCategory === 'Area') {
            const currentVariants = variantsRef.value;
            const response = await updateAreaMutation.mutate({
                allocationId,
                start: start.toISO(),
                end: end.toISO(),
                isOptional,
                createPhases,
                updatePhases,
                deletePhases,
            });
            if (response && response.data) {
                const updatedAllocation = response.data.update?.allocation;

                if (updatedAllocation) {
                    // validate result
                    const confirmedPhases = validateAllocationUpdateResponse(updatedAllocation, remainingPhases);
                    if (isOptional !== updatedAllocation.isOptional) {
                        throw Error(`isOptional of allocation ${updatedAllocation.id} not updated correctly`);
                    }

                    const updatedVariants = currentVariants.map((v) => {
                        if (v.id === variantId) {
                            return {
                                ...v,
                                allocations: {
                                    nodes: v.allocations.nodes.map((n) => {
                                        if (n.id === updatedAllocation.id) {
                                            return mapEventAllocationForTimeline(
                                                {
                                                    ...v,
                                                    referenceAllocationRoomId:
                                                        updatedAllocation.variant.referenceAllocationRoomId ?? null,
                                                    referenceAllocationAreaId:
                                                        updatedAllocation.variant.referenceAllocationAreaId ?? null,
                                                    event: {
                                                        id: event.value?.id ?? '',
                                                        nameShort: event.value?.nameShort ?? '',
                                                    },
                                                },
                                                updatedAllocation,
                                                locationTypeCategory,
                                                executionPhasePosition.value,
                                                areAllocationsEditable,
                                            );
                                        }
                                        return n;
                                    }),
                                },
                            };
                        } else {
                            return v;
                        }
                    });
                    setVariantsRef(updatedVariants);
                    return {
                        phaseUpdates: confirmedPhases,
                        isOptional,
                        referenceAllocationId: referenceAllocationId ?? null,
                    };
                }
            }
            throw Error('Error on allocationArea update without duplicate');
        }
        throw Error('Unknown LocationTypeCategory on allocation update');
    }

    const duplicateRoomMutation = useMutation(AllocationRoomDuplicateDocument, { fetchPolicy: 'no-cache' });
    const duplicateAreaMutation = useMutation(AllocationAreaDuplicateDocument, { fetchPolicy: 'no-cache' });

    async function duplicateAllocation(
        variantId: string,
        allocationId: string,
        newLocations: CopyTargetLocation[],
        locationTypeCategory: LocationTypeCategory,
    ) {
        const { roomIds, areaIds } = splitCopyTargetsByLocationType(newLocations);

        let response = undefined;
        let mutation = undefined;

        switch (locationTypeCategory) {
            case 'Room':
                mutation = duplicateRoomMutation;
                break;
            case 'Area':
                mutation = duplicateAreaMutation;
                break;
        }

        if (mutation) {
            response = await mutation.mutate({
                variantId,
                allocationId,
                roomIds,
                areaIds,
            });
        }

        if (!response) {
            throw new Error('Unknown locationTypeCategory on duplicateAllocation');
        }

        if (response.errors) {
            throw new Error(`Duplicating allocations failed ${JSON.stringify(response.errors, null, 2)}`);
        }

        const updatedVariant = response.data?.duplicate?.variant;
        if (updatedVariant) {
            for (const id of [...roomIds, ...areaIds]) {
                store.incrementLocationAllocationTotalCount(id);
            }
            const newValue = variantsRef.value.map((v) => {
                if (v.id === updatedVariant.id) {
                    return mapEventVariantForTimeline(
                        updatedVariant,
                        timelineStart.value,
                        timelineEnd.value,
                        executionPhasePosition.value,
                        areAllocationsEditable,
                    );
                }
                return v;
            });
            setVariantsRef(newValue);
        }
    }

    const duplicateAndUpdateRoomMutation = useMutation(AllocationRoomUpdateAndDuplicateDocument, {
        fetchPolicy: 'no-cache',
    });
    const duplicateAndUpdateAreaMutation = useMutation(AllocationAreaUpdateAndDuplicateDocument, {
        fetchPolicy: 'no-cache',
    });

    async function duplicateAndUpdateAllocation(
        variantId: string,
        allocationId: string,
        newLocations: CopyTargetLocation[],
        locationTypeCategory: LocationTypeCategory,
        start: DateTime,
        end: DateTime,
        isOptional: boolean,
        createPhases: AllocationPhaseInput[],
        updatePhases: AllocationPhaseUpdateInputFlat[],
        deletePhases: AllocationPhaseDeleteInput[],
        referenceAllocationId?: string | null,
    ) {
        if (referenceAllocationId !== undefined) {
            const currentVariants = variantsRef.value;
            const variantToUpdate = currentVariants.find((variant) => variant.id === variantId);
            if (variantToUpdate) {
                let referenceAllocationAreaId = null;
                let referenceAllocationRoomId = null;
                if (referenceAllocationId !== null) {
                    const refAllocation = variantToUpdate.allocations.nodes.find(
                        (allocation) => allocation.id === referenceAllocationId,
                    );
                    if (refAllocation) {
                        referenceAllocationAreaId =
                            refAllocation.locationTypeCategory === 'Room' ? null : referenceAllocationId;
                        referenceAllocationRoomId =
                            refAllocation.locationTypeCategory === 'Room' ? referenceAllocationId : null;
                    }
                }
                const response = await updateReferenceAlloctionMutation.mutate({
                    variantId,
                    referenceAllocationAreaId,
                    referenceAllocationRoomId,
                });
                if (response?.errors) {
                    throw Error(`Could not update variant reference allocatio id ${response.errors}`);
                }
                if (response?.data) {
                    // Refresh every copy of the id, not just the variant's own field. Each allocation
                    // carries one on `allocationVariant`, baked in when the variant was mapped, and the
                    // Timeline store is seeded from it — so leaving it stale made the reference revert
                    // whenever a variant's allocations all unmounted and remounted.
                    const updatedVariants = currentVariants.map((v) =>
                        v.id === variantId
                            ? {
                                  ...v,
                                  referenceAllocationId,
                                  allocations: {
                                      ...v.allocations,
                                      nodes: v.allocations.nodes.map((allocation) => ({
                                          ...allocation,
                                          allocationVariant: {
                                              ...allocation.allocationVariant,
                                              referenceAllocationId,
                                          },
                                      })),
                                  },
                              }
                            : v,
                    );
                    setVariantsRef(updatedVariants);
                }
            }
        }

        const { roomIds, areaIds } = splitCopyTargetsByLocationType(newLocations);

        let response = undefined;
        let mutation = undefined;

        switch (locationTypeCategory) {
            case 'Room':
                mutation = duplicateAndUpdateRoomMutation;
                break;
            case 'Area':
                mutation = duplicateAndUpdateAreaMutation;
                break;
        }

        if (mutation) {
            response = await mutation.mutate({
                variantId,
                allocationId,
                updateAllocation: {
                    start: start.toISO(),
                    end: end.toISO(),
                    isOptional,
                },
                createPhases,
                updatePhases,
                deletePhases: deletePhases.map((p) => p.id),
                roomIds,
                areaIds,
            });
        }

        if (!response) {
            throw new Error('Unknown locationTypeCategory on duplicateAndUpdateAllocation');
        }

        if (response.errors) {
            throw new Error(
                `Duplicating allocations and updating phases failed ${JSON.stringify(response.errors, null, 2)}`,
            );
        }

        const updatedVariant = response.data?.updateAndDuplicate?.variant;

        if (updatedVariant) {
            const currentVariants = variantsRef.value;
            for (const id of [...roomIds, ...areaIds]) {
                store.incrementLocationAllocationTotalCount(id);
            }
            const mappedUpdatedVariant = mapEventVariantForTimeline(
                updatedVariant,
                timelineStart.value,
                timelineEnd.value,
                executionPhasePosition.value,
                areAllocationsEditable,
            );
            const newValue = currentVariants.map((v) => (v.id === updatedVariant.id ? mappedUpdatedVariant : v));
            setVariantsRef(newValue);

            let updatedAllocation = undefined;
            switch (locationTypeCategory) {
                case 'Room':
                    updatedAllocation = updatedVariant.rooms.nodes.find((n) => n.id === allocationId);
                    break;
                case 'Area':
                    updatedAllocation = updatedVariant.areas.nodes.find((n) => n.id === allocationId);
                    break;
            }
            return updatedAllocation;
        }
        return undefined;
    }

    const timelineLoading = computed(() => {
        return loading.value || timelineDataProcessing.value;
    });

    return {
        variantsRef,
        setVariantsRef,
        timelineLoading,
        error,
        onResult,
        site,
        event,
        rooms,
        areas,
        timelineHeaderName,
        timelineHeaderLinkTarget,
        defaultPhaseTypeId,
        executionPhasePosition,
        usages,
        createAllocation,
        updateAllocationData,
        duplicateAllocation,
        duplicateAndUpdateAllocation,
    };
}
